import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  FaTools,
  FaTh,
  FaList,
  FaStar,
  FaEdit,
  FaBox,
} from "react-icons/fa";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api/axiosInstance";
import getImageUrl from "../../../utils/imageUtils";
import AddToSetupModal from "../../../components/setup/AddToSetupModal";
import styles from "../../../styles/pages/profile/SetupPage.module.css";

/* ── helpers ─────────────────────────────────────────── */

const STATUS_CLASS = {
  own: styles.statusOwn,
  wishlist: styles.statusWishlist,
  sold: styles.statusSold,
};
const STATUS_LABEL = { own: "Own It", wishlist: "Wishlist", sold: "Sold" };

function StarRow({ rating }) {
  if (!rating) return null;
  return (
    <span className={styles.stars}>
      {Array.from({ length: rating }).map((_, i) => (
        <FaStar key={i} size={10} />
      ))}
    </span>
  );
}

function ProductThumb({ product, className, placeholderClass }) {
  const src = (() => {
    const imgs = product?.images;
    if (Array.isArray(imgs) && imgs.length) return getImageUrl(imgs[0]);
    return null;
  })();
  if (src)
    return <img src={src} alt={product.name} className={className} />;
  return (
    <div className={placeholderClass}>
      <FaBox size={20} />
    </div>
  );
}

function MissingProductBody({ item }) {
  return (
    <>
      <p className={styles.gridProductName}>Product no longer available</p>
      <div className={styles.gridMeta}>
        <span className={`${styles.statusBadge} ${STATUS_CLASS[item.status] || ""}`}>
          {STATUS_LABEL[item.status] || item.status}
        </span>
      </div>
    </>
  );
}

/* ── GridCard ────────────────────────────────────────── */
function GridCard({ item, onEdit }) {
  const product = item?.product;
  const editButton = (
    <button
      type="button"
      className={styles.editBtn}
      onClick={(e) => { e.preventDefault(); onEdit(item); }}
      title="Edit"
    >
      <FaEdit size={12} />
    </button>
  );

  if (!product?._id) {
    return (
      <div className={styles.gridCard}>
        <div className={styles.gridImgPlaceholder}>
          <FaBox size={20} />
        </div>
        <div className={styles.gridBody}>
          <MissingProductBody item={item} />
        </div>
        {editButton}
      </div>
    );
  }

  return (
    <div className={styles.gridCard}>
      <Link to={`/product/${product._id}`} style={{ textDecoration: "none", display: "block" }}>
        <ProductThumb
          product={product}
          className={styles.gridImg}
          placeholderClass={styles.gridImgPlaceholder}
        />
        <div className={styles.gridBody}>
          <p className={styles.gridProductName}>{product.name}</p>
          {product.brand?.name && (
            <p className={styles.gridBrand}>{product.brand.name}</p>
          )}
          <div className={styles.gridMeta}>
            <span className={`${styles.statusBadge} ${STATUS_CLASS[item.status] || ""}`}>
              {STATUS_LABEL[item.status] || item.status}
            </span>
            <StarRow rating={item.rating} />
          </div>
          {item.note && (
            <p style={{ margin: "6px 0 0", fontSize: "0.76rem", color: "var(--color-text-secondary)", fontFamily: "var(--font-family)", fontStyle: "italic", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              "{item.note}"
            </p>
          )}
        </div>
      </Link>
      {editButton}
    </div>
  );
}

/* ── ListCard ────────────────────────────────────────── */
function ListCard({ item, onEdit }) {
  const product = item?.product;
  const editButton = (
    <button
      type="button"
      className={styles.listEditBtn}
      onClick={(e) => { e.stopPropagation(); onEdit(item); }}
    >
      <FaEdit size={11} /> Edit
    </button>
  );

  if (!product?._id) {
    return (
      <div className={styles.listCard}>
        <div className={styles.listThumbPlaceholder}>
          <FaBox size={18} />
        </div>
        <div className={styles.listInfo}>
          <MissingProductBody item={item} />
        </div>
        {editButton}
      </div>
    );
  }

  return (
    <div className={styles.listCard} onClick={() => {}}>
      <Link
        to={`/product/${product._id}`}
        style={{ display: "contents", textDecoration: "none" }}
      >
        <ProductThumb
          product={product}
          className={styles.listThumb}
          placeholderClass={styles.listThumbPlaceholder}
        />
        <div className={styles.listInfo}>
          <p className={styles.listProductName}>{product.name}</p>
          {product.brand?.name && (
            <p className={styles.listBrand}>{product.brand.name}</p>
          )}
          {item.note && <p className={styles.listNote}>"{item.note}"</p>}
        </div>
        <div className={styles.listRight}>
          <span className={`${styles.statusBadge} ${STATUS_CLASS[item.status] || ""}`}>
            {STATUS_LABEL[item.status] || item.status}
          </span>
          <StarRow rating={item.rating} />
          {item.ownedSince && (
            <span className={styles.ownedSince}>
              Since {new Date(item.ownedSince).getFullYear()}
            </span>
          )}
          {item.rating === 0 && !item.ownedSince && null}
        </div>
      </Link>
      {editButton}
    </div>
  );
}

/* ── SetupPage ───────────────────────────────────────── */
const SetupPage = () => {
  const queryClient = useQueryClient();
  const [view, setView] = useState("grid"); // "grid" | "list"
  const [editingItem, setEditingItem] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["mySetup"],
    queryFn: async () => {
      const res = await api.get("/setup");
      return res.data;
    },
    staleTime: 60_000,
  });

  const grouped = data?.grouped || {};
  const categoryNames = Object.keys(grouped).sort();
  const totalCount = data?.items?.length || 0;

  const handleSaved = useCallback(
    (updatedItem) => {
      queryClient.invalidateQueries({ queryKey: ["mySetup"] });
      setEditingItem(null);
    },
    [queryClient]
  );

  const handleRemoved = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["mySetup"] });
    setEditingItem(null);
  }, [queryClient]);

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>
            <FaTools size={20} /> My Setup
          </h2>
          <p className={styles.subtitle}>
            {totalCount === 0
              ? "Add products to build your public tech biography"
              : `${totalCount} item${totalCount !== 1 ? "s" : ""} across ${categoryNames.length} categor${categoryNames.length !== 1 ? "ies" : "y"}`}
          </p>
        </div>

        <div className={styles.viewToggle}>
          <button
            type="button"
            className={`${styles.toggleBtn} ${view === "grid" ? styles.toggleBtnActive : ""}`}
            onClick={() => setView("grid")}
          >
            <FaTh size={12} /> Grid
          </button>
          <button
            type="button"
            className={`${styles.toggleBtn} ${view === "list" ? styles.toggleBtnActive : ""}`}
            onClick={() => setView("list")}
          >
            <FaList size={12} /> List
          </button>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className={styles.empty}>
          <p className={styles.emptyHint}>Loading your setup…</p>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && totalCount === 0 && (
        <div className={styles.empty}>
          <FaTools size={40} className={styles.emptyIcon} />
          <p className={styles.emptyText}>Your Setup is empty</p>
          <p className={styles.emptyHint}>
            Go to any product page and tap <strong>"Add to Setup"</strong> to start building your public tech inventory.
          </p>
        </div>
      )}

      {/* Categories */}
      {!isLoading &&
        categoryNames.map((cat) => (
          <div key={cat} className={styles.categoryGroup}>
            <div className={styles.categoryHeader}>
              <h3 className={styles.categoryName}>{cat}</h3>
              <span className={styles.categoryCount}>
                {grouped[cat].length}
              </span>
            </div>

            {view === "grid" ? (
              <div className={styles.grid}>
                {grouped[cat].map((item) => (
                  <GridCard key={item._id} item={item} onEdit={setEditingItem} />
                ))}
              </div>
            ) : (
              <div className={styles.list}>
                {grouped[cat].map((item) => (
                  <ListCard key={item._id} item={item} onEdit={setEditingItem} />
                ))}
              </div>
            )}
          </div>
        ))}

      {/* Edit/Remove modal */}
      {editingItem && (
        <AddToSetupModal
          product={editingItem.product}
          existingItem={editingItem}
          onClose={() => setEditingItem(null)}
          onSaved={handleSaved}
          onRemoved={handleRemoved}
        />
      )}
    </div>
  );
};

export default SetupPage;
